import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building,
  Building2,
  Home,
  Users,
  ShoppingBag,
  Wrench,
  School,
  ArrowUp,
  ArrowDown,
  ArrowRight
} from 'lucide-react';
import './Industries.css';

const Industries = () => {
  const location = useLocation();
  const [activeIndustry, setActiveIndustry] = useState(0);

  const industries = useMemo(
    () => [
      {
        name: 'Professional Services',
        slug: 'professional-services',
        Icon: Building,
        tagline:
          'Technology that sharpens expertise and streamlines how work gets done.',
        description:
          'Professional services depend on precision, responsiveness, and efficiency. Disconnected tools, slow systems, and manual processes can quietly drain time and impact client experience. Snap Remedy helps businesses strengthen both their external presence and their internal operations. From polished websites that build credibility to integrated systems that improve collaboration, document management, scheduling, and client communication, we ensure your technology supports the way your team actually works. The result is smoother workflows, better use of time, and a technology foundation that scales as your firm grows.',
        useCases: [
          'Integrated document management and secure file sharing',
          'Workflow automation for client intake, approvals, and follow-ups',
          'CRM and client communication system integration',
          'Secure remote access and collaboration tools for hybrid teams'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'Turnaround time on client requests – increasing speed from request to response or delivery',
          Icon2: ArrowDown,
          label2:
            'Data accessibility and retrieval time – how quickly teams can find client info'
        }
      },
      {
        name: 'Manufacturing & Fabrication',
        slug: 'manufacturing-fabrication',
        Icon: Building2,
        tagline:
          'Technology that streamlines operations and puts your capabilities on display.',
        description:
          'Manufacturing and fabrication businesses rely on efficient workflows, accurate data, and systems that keep production moving without friction. When critical information is hard to access or tools don’t work well together, delays and costly mistakes follow. Snap Remedy helps manufacturers improve internal efficiency by connecting systems, improving data access, and supporting tools that make operations smoother and more reliable. At the same time, we strengthen your external presence with websites and digital tools that clearly showcase your capabilities, improve findability, and attract the right customers and partners. With the right technology in place, your business gains the clarity and efficiency needed to operate better—and the visibility needed to grow.',
        useCases: [
          'Integration between production, inventory, and accounting systems',
          'Workflow automation to reduce manual data entry and errors',
          'Connectivity across all devices within an organization',
          'Monitoring key data for proactive alerting when issues are detected'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'System uptime – downtime directly impacts production and revenue',
          Icon2: ArrowDown,
          label2:
            'Order-to-delivery cycle time – speed from production start to fulfillment'
        }
      },
      {
        name: 'Retail',
        slug: 'retail',
        Icon: ShoppingBag,
        tagline:
          'Technology that keeps stores connected and customers coming back.',
        description:
          'Retail moves fast, and technology has to keep up. From on-demand support when systems hiccup to ensuring data flows smoothly between locations, disconnected tools can quickly create silos that impact sales and customer experience. Snap Remedy helps retail businesses stay agile by connecting systems, supporting point-of-sale and back-office tools, and responding quickly when issues arise. We also help retailers expand into eCommerce and online sales, building web experiences that showcase what makes each brand special while creating seamless paths for customers to buy—wherever they are.',
        useCases: [
          'Websites that highlight brand identity and drive customer engagement',
          'Rapid-response technical support for on-demand issues',
          'POS, inventory, and accounting system integration',
          'eCommerce setup and online sales integration'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'Inventory accuracy – real-time stock data across locations and channels',
          Icon2: ArrowDown,
          label2:
            'Incident response time – speed of support when issues affect sales'
        }
      },
      {
        name: 'Property Management & Real Estate Groups',
        slug: 'property-management-real-estate-groups',
        Icon: Home,
        tagline:
          'Technology that keeps you informed, responsive, and always one step ahead.',
        description:
          'In real estate and property management, timing and access to information are everything. Agents and managers need to find data quickly, communicate clearly, and respond without delays. Snap Remedy helps streamline the systems that support listings, tenant information, documents, and communication, making critical information easy to access when decisions need to be made fast. We also support tools and platforms that strengthen client relationships—helping you stay organized, responsive, and connected long after the deal is done. The result is technology that supports trust, efficiency, and long-term client relationships.',
        useCases: [
          'Onsite hardware/network installation and configuration',
          'Secure access and permissions for agents and teams',
          'Centralized document and data management systems',
          'Workflow automation for listings, approvals, and onboarding'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'Data accuracy and consistency – ensuring listings and records stay up to date',
          Icon2: ArrowDown,
          label2:
            'Data access time – how quickly agents can retrieve listings, documents, and client info'
        }
      },
      {
        name: 'Non-profits / Unions',
        slug: 'non-profits-unions',
        Icon: Users,
        tagline:
          'Technology that simplifies operations and supports your mission.',
        description:
          'Non-profits and unions rely on specialized systems to manage members, communications, and day-to-day operations—often with limited time and resources. Generic tools rarely fit the way these organizations work. Snap Remedy designs and supports technology solutions tailored to these needs, from member management and communication platforms to internal systems that reduce manual effort and improve efficiency. By building tools that simplify workflows and save time, we help organizations stay focused on serving their members and advancing their mission, not wrestling with technology.',
        useCases: [
          'Member management and database systems',
          'Websites and digital platforms that clearly communicate mission and value',
          'Knowledgebase of secure digitized documents for quick retrieval',
          'Live technical support for software and hardware environments'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'Communication effectiveness – reach and engagement across member channels',
          Icon2: ArrowDown,
          label2:
            'Staff time saved through automation – reducing manual administrative work'
        }
      },
      {
        name: 'Education & Community Services',
        slug: 'education-community-services',
        Icon: School,
        tagline:
          'Technology that supports people, relationships, and learning.',
        description:
          'Community service organizations and education providers are built on strong relationships and careful attention to detail. With staff focused on supporting students and communities, technology should make work easier—not add complexity. Snap Remedy works closely with organizations in this space to create efficient systems that support daily operations, communication, and data management. We help streamline workflows, support the tools your teams rely on, and build inspiring web presences that clearly communicate your mission and help new students and families find you. The result is technology that quietly supports your work while amplifying the impact you make.',
        useCases: [
          'Inspiring, easy-to-navigate websites that clearly communicate products and values',
          'Workflow automation for enrollment, scheduling, and reporting',
          'Integrated communication tools for timely updates to clients and customers',
          'Centralized client information systems for accurate, up-to-date records'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'Communication effectiveness – clear, timely messaging with students and families',
          Icon2: ArrowDown,
          label2:
            'Administrative inefficiency – time saved through streamlined workflows'
        }
      },
      {
        name: 'Construction & Trades',
        slug: 'construction-trades',
        Icon: Wrench,
        tagline:
          'Technology that strengthens your business off-site and wins work on-site.',
        description:
          'Construction and trades companies do their best work in the field, but growth depends on what happens off-site. A weak website, outdated systems, or disconnected tools can make it harder to attract new customers, win partners, and showcase completed projects. Snap Remedy helps modernize the technology that supports your business—from building a strong, professional web presence to integrating systems that keep communication, scheduling, and documentation organized behind the scenes. We focus on making your business easy to find, easy to trust, and easy to work with, so your craftsmanship is matched by the technology that represents it.',
        useCases: [
          'Professional websites that showcase work, build trust and attract new business',
          'Automated quoting, invoicing, and scheduling workflows',
          'Integrated tools for managing partners and subcontractors',
          'Centralized client, project, and job data management'
        ],
        stats: {
          Icon1: ArrowUp,
          label1:
            'Increasing awareness – showcase reliability, craftsmanship and professionalism',
          Icon2: ArrowDown,
          label2:
            'Administrative time per project – time spent on non-billable office tasks'
        }
      }
    ],
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slugFromUrl = params.get('industry');

    if (!slugFromUrl) {
      setActiveIndustry(0);
      return;
    }

    const matchedIndex = industries.findIndex(
      (industry) => industry.slug === slugFromUrl
    );

    setActiveIndustry(matchedIndex !== -1 ? matchedIndex : 0);
  }, [location.search, industries]);

  const active = industries[activeIndustry];

  return (
    <div className="industries">
      <section className="hero">
        <div className="hero-container">
          <motion.div className="industries-hero-content">
            <div className="hero-label">
              <span className="label-dot"></span>
              Industries We Commonly Partner With
            </div>
            <h1 className="hero-title">
              Trusted IT Solutions
              <span className="highlight">For Every Industry</span>
            </h1>
            <p className="hero-description">
              Every industry runs into technical hurdles—whether from rapid growth,
              outdated systems, or unexpected disruptions. That’s why the right IT
              support team matters. We help businesses make smart, steady decisions
              as they overcome challenges and scale. Built on reliability, ingenuity
              and ownership we act as a trusted partner—solving problems, guiding
              strategy, and keeping your technology working for you.
            </p>
          </motion.div>

          <div>
            <img
              src="hero-snap-remedy-industries.svg"
              className="hero-img"
              alt="Illustration of people and a big globe"
            />
          </div>
        </div>
      </section>

      <section
        id="industry-explorer"
        className="industry-explorer section-spacing"
      >
        <div className="container">
          <div className="explorer-layout">
            <div className="industry-nav">
              {industries.map((industry, index) => {
                const NavIcon = industry.Icon;

                return (
                  <motion.button
                    key={industry.slug}
                    type="button"
                    className={`industry-nav-item ${
                      activeIndustry === index ? 'active' : ''
                    }`}
                    onClick={() => setActiveIndustry(index)}
                  >
                    <span className="industry-icon">
                      <NavIcon size={32} />
                    </span>
                    <span className="industry-name">{industry.name}</span>
                    <span className="industry-arrow">
                      <ArrowRight size={16} />
                    </span>
                  </motion.button>
                );
              })}
            </div>

            <div className="industry-content">
              <AnimatePresence mode="wait">
                <motion.div key={activeIndustry} className="industry-details">
                  <div className="industry-header">
                    <div>
                      <h2 className="industry-title">{active.name}</h2>
                      <p className="industry-tagline">{active.tagline}</p>
                    </div>
                  </div>

                  <p className="industry-description">{active.description}</p>

                  <div className="use-cases">
                    <h3 className="use-cases-title">Key Use Cases</h3>
                    <ul className="use-cases-list">
                      {active.useCases.map((useCase, i) => (
                        <li key={i}>{useCase}</li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/contact" className="industry-cta">
                    Discuss Your {active.name} Needs →
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      <section className="industries-cta section-spacing">
        <div className="container">
          <motion.div className="cta-box">
            <h2 className="cta-title">
              Technology Solutions for Every Type of Business
            </h2>
            <p className="cta-description">
              Our expertise extends far beyond the industries listed above. We work
              with growing businesses across sectors such as hospitality, logistics,
              healthcare, and many others that are evolving and outgrowing their
              current technology. Whether you’re expanding your team, adding new
              systems, or increasing operational complexity, we help organizations
              build the technical capability they need to operate more efficiently
              and move forward with confidence.
            </p>
            <Link to="/contact#contact-form" className="btn-cta-primary">
              Let’s Get Started
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;